import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_COMPANY_API_BASE || "/api",
});

// Market API インスタンス（市場サーバーへの直接通信）
const marketApi = axios.create({
  baseURL: process.env.REACT_APP_MARKET_API_BASE || "/market-api",
});
marketApi.interceptors.response.use((response) => {
  const data = response.data;
  if (data && data.result === "NG") {
    const error = new Error(data.errorMessage || "Market API error");
    error.response = response;
    error.apiError = true;
    throw error;
  }
  return response;
});

// Response interceptor: { result: "NG" } をエラーとして扱う
api.interceptors.response.use((response) => {
  const data = response.data;
  if (data && data.result === "NG") {
    const error = new Error(data.errorMessage || "API error");
    error.response = response;
    error.apiError = true;
    throw error;
  }
  return response;
});

// ── 認証 ──

/**
 * 担当者サインイン
 * @returns { result: "OK1"|"OK2", returnValue: companyId, loginAddress: email, members?: [] }
 * OK1: 1名マッチ → そのままホームへ
 * OK2: 複数名マッチ → メンバー選択画面へ
 */
export const memberSignIn = async (data) => {
  const res = await api.post("/member/signIn", data);
  return res.data;
};

/**
 * 企業情報を取得
 */
export const fetchCompany = async (companyId) => {
  const res = await api.get(`/companies/${companyId}`);
  return res.data;
};

/**
 * メンバー情報を取得（メールアドレス + 企業ID + パスワードで検索）
 */
export const fetchMemberByEmail = async (email, companyId, password) => {
  const res = await api.get(
    `/members/email/${encodeURIComponent(email)}/company/${companyId}/password/${encodeURIComponent(password)}`
  );
  return res.data;
};

/**
 * 企業ドメイン情報を取得（ホーム画面の「目的」表示に使用）
 */
export const fetchEnterpriseDomain = async (companyId) => {
  const res = await api.get(`/enterpriseDomains/company/${companyId}`);
  return res.data;
};

// ── ワーカー ──

/**
 * メンバーの利用可能なワーカー一覧を取得
 */
export const fetchAvailableWorkers = async (memberId) => {
  const res = await api.get(`/workers/avarable/${memberId}`);
  return res.data;
};

/**
 * ワーカー詳細を取得
 */
export const fetchWorkerDetail = async (workerId) => {
  const res = await api.get(`/workers/worker/${workerId}`);
  return res.data;
};

/**
 * メンバーの解任済みワーカー一覧を取得
 */
export const fetchReleasedWorkers = async (memberId) => {
  const res = await api.get(`/workers/released/${memberId}`);
  return res.data;
};

/**
 * ワーカーに紐づくワーク一覧を取得（解任済みワーカーの作業履歴参照用）
 */
export const fetchWorksByWorker = async (workerId) => {
  const res = await api.get(`/workers/${workerId}/works`);
  return res.data;
};

// ── ワークフロー ──

/**
 * プロジェクトのワークフロー一覧を取得
 */
export const fetchWorkflowsByProject = async (projectId) => {
  const res = await api.get(`/workflow/project/${projectId}`);
  return res.data;
};

/**
 * プログラムのワークフロー一覧を取得
 */
export const fetchWorkflowsByProgram = async (programId) => {
  const res = await api.get(`/workflow/program/${programId}`);
  return res.data;
};

// ── 作業（Work）──

/**
 * ワークフローのToDo作業一覧を取得
 */
export const fetchTodoWorks = async (workflowId) => {
  const res = await api.get(`/works/todo/${workflowId}`);
  return res.data;
};

/**
 * ワークフローの実行中作業一覧を取得
 */
export const fetchDoingWorks = async (workflowId) => {
  const res = await api.get(`/works/doing/${workflowId}`);
  return res.data;
};

/**
 * ワークフローの完了作業一覧を取得
 */
export const fetchDoneWorks = async (workflowId) => {
  const res = await api.get(`/works/done/${workflowId}`);
  return res.data;
};

/**
 * 作業詳細を取得
 */
export const fetchWorkDetail = async (workId) => {
  const res = await api.get(`/works/${workId}`);
  return res.data;
};

/**
 * 作業を開始する（ToDo → Doing）
 */
export const startWork = async (data) => {
  const res = await api.put("/works/doing", data);
  return res.data;
};

/**
 * 作業を完了する（Doing → Done）
 */
export const completeWork = async (data) => {
  const res = await api.put("/works/done", data);
  return res.data;
};

// ── プロジェクト / プログラム ──

/**
 * プロジェクト詳細取得（associatedActionPlans を含む）
 */
export const fetchProject = async (projectId) => {
  const res = await api.get(`/projects/${projectId}`);
  return res.data;
};

/**
 * プログラム詳細取得（associatedActionPlans を含む）
 */
export const fetchProgram = async (programId) => {
  const res = await api.get(`/programs/${programId}`);
  return res.data;
};

// ── ワークフロー CRUD ──

/**
 * ワークフロー詳細取得（actionPlanId を含む）
 */
export const fetchWorkflowDetail = async (workflowId) => {
  const res = await api.get(`/workflow/${workflowId}`);
  return res.data;
};

/**
 * プロジェクトのワークフローを新規登録
 * body: { companyId, projectId, actionPlanId, workflowName, workflowDescription,
 *         startDate, endDate, businessState }
 */
export const createProjectWorkflow = async (data) => {
  const res = await api.post("/workflows/project", data);
  return res.data;
};

/**
 * プログラムのワークフローを新規登録
 */
export const createProgramWorkflow = async (data) => {
  const res = await api.post("/workflows/program", data);
  return res.data;
};

/**
 * ワークフローを更新
 * body: { companyId, workflowId, projectId?, programId?, actionPlanId,
 *         workflowName, workflowDescription, startDate, endDate, businessState }
 */
export const updateWorkflow = async (data) => {
  const res = await api.put("/workflows", data);
  return res.data;
};

/**
 * ワークフローを削除
 */
export const deleteWorkflow = async (workflowId) => {
  const res = await api.delete(`/workflows/${workflowId}`);
  return res.data;
};

/**
 * アクションプランを取得（配下の actions[] を含む）
 */
export const fetchActionPlan = async (actionPlanId) => {
  const res = await api.get(`/actionPlans/${actionPlanId}`);
  return res.data;
};

/**
 * アクション詳細を取得（applicationUrl / personUrl を含む）
 */
export const fetchAction = async (actionId) => {
  const res = await api.get(`/actions/${actionId}`);
  return res.data;
};

// ── 作業 CRUD ──

/**
 * 作業を新規登録
 * body: { companyId, actionId, memberId, memberName, workflowId,
 *         workName, workDescription, workerId, applicationUrl?, personUrl? }
 */
export const createWork = async (data) => {
  const res = await api.post("/works", data);
  return res.data;
};

/**
 * ToDo 作業を削除
 */
export const deleteTodoWork = async (workId) => {
  const res = await api.delete(`/works/todo/${workId}`);
  return res.data;
};

/**
 * 作業を評価する（Done → Evaluated）
 * body: { workId, endDate, companyId, evaluationNumber }
 */
export const evaluateWork = async (data) => {
  const res = await api.put("/works/evaluated", data);
  return res.data;
};

/**
 * 完了作業の開始・終了日時（およびworkName/workDescription）を更新
 * body: { workId, startDate, endDate, workName, workDescription }
 */
export const updateDoneWork = async (data) => {
  const res = await api.put("/works/edit/done", data);
  return res.data;
};

/**
 * 完了作業を削除
 */
export const deleteDoneWork = async (workId) => {
  const res = await api.delete(`/works/delete/done/${workId}`);
  return res.data;
};

/**
 * ToDo作業のworkName/workDescriptionを更新
 * body: { workId, workName, workDescription }
 */
export const updateTodoWork = async (data) => {
  const res = await api.put("/works/edit/todo", data);
  return res.data;
};

// ── ワーカー目標 / 実績 ──

/**
 * ワーカーの指標一覧を取得（Job → Indicators）
 */
export const fetchWorkerIndicators = async (workerId) => {
  const res = await api.get(`/workers/indicator/${workerId}`);
  return res.data;
};

/**
 * ワーカー目標を追加
 * body: { workerId, goal: { indicator, plannedValue, plannedDate, deadline,
 *         goalOwnerId, goalOwnerName, goalOwnerType, createdGoalAt } }
 */
export const addWorkerGoal = async (data) => {
  const res = await api.post("/workers/goal/", data);
  return res.data;
};

/**
 * ワーカー目標を更新
 * body: { workerId, goal: { ...existing, plannedValue, deadline, updatedGoalAt } }
 */
export const updateWorkerGoal = async (data) => {
  const res = await api.put("/workers/goal/", data);
  return res.data;
};

/**
 * ワーカー目標を削除
 * body: { workerId, goal: goalObject }
 */
export const deleteWorkerGoal = async (data) => {
  const res = await api.delete("/workers/goal/", { data });
  return res.data;
};

/**
 * ワーカー実績を追加
 * body: { workerId, achievement: { goal, actualValue, actualDate, createdAchievementAt } }
 */
export const addWorkerAchievement = async (data) => {
  const res = await api.post("/workers/achievement/", data);
  return res.data;
};

/**
 * ワーカー実績を削除
 * body: { workerId, achievement: achievementObject }
 */
export const deleteWorkerAchievement = async (data) => {
  const res = await api.delete("/workers/achievement/", { data });
  return res.data;
};

// ── ガイドライン ──

/**
 * ワークのガイドライン一覧を取得
 * GET /guidelines/work/{workId}
 */
export const fetchWorkGuidelines = async (workId) => {
  const res = await api.get(`/guidelines/work/${workId}`);
  return res.data;
};

// ── インプットデータ（データカテゴリ） ──

/**
 * ワークのインプットデータカテゴリ一覧を取得
 * GET /dataCategories/work/input/{workId}
 */
export const fetchWorkInputDataCategories = async (workId) => {
  const res = await api.get(`/dataCategories/work/input/${workId}`);
  return res.data;
};

// ── アウトプットデータ ──

/**
 * ワークのアウトプットデータ一覧を取得
 * GET /data/work/{workId}
 */
export const fetchWorkOutputData = async (workId) => {
  const res = await api.get(`/data/work/${workId}`);
  return res.data;
};

/**
 * ワークのアウトプット用データカテゴリ一覧を取得（データ追加時の選択肢）
 * GET /dataCategories/work/output/{workId}
 */
export const fetchWorkOutputDataCategories = async (workId) => {
  const res = await api.get(`/dataCategories/work/output/${workId}`);
  return res.data;
};

/**
 * アウトプットデータを追加
 * POST /data
 * body: { companyId, workId, dataName, dataCategoryId, dataCategoryName, dataDescription, url }
 */
export const createOutputData = async (data) => {
  const res = await api.post("/data", data);
  return res.data;
};

/**
 * アウトプットデータを更新
 * PUT /data
 */
export const updateOutputData = async (data) => {
  const res = await api.put("/data", data);
  return res.data;
};

/**
 * アウトプットデータを削除
 * DELETE /data/{dataId}
 */
export const deleteOutputData = async (dataId) => {
  const res = await api.delete(`/data/${dataId}`);
  return res.data;
};

/**
 * データタイプのデータ項目一覧を取得
 * GET /dataItems/dataType/{dataTypeId}
 */
export const fetchDataItemsByDataType = async (dataTypeId) => {
  const res = await api.get(`/dataItems/dataType/${dataTypeId}`);
  return res.data;
};

// ── 評価（Evaluation）──

/**
 * 対象（ワーク等）の評価一覧を取得
 * GET /evaluations/{targetId}
 */
export const fetchEvaluations = async (targetId) => {
  const res = await api.get(`/evaluations/${targetId}`);
  return res.data;
};

/**
 * 評価を登録
 * POST /evaluations
 * body: { companyId, evaluationTarget, targetId, evaluationName,
 *         evaluationNumber, evaluationDescription, evaluator }
 */
export const createEvaluation = async (data) => {
  const res = await api.post("/evaluations", data);
  return res.data;
};

/**
 * 評価を更新
 * PUT /evaluations
 * body: { evaluationId, companyId, evaluationTarget, targetId,
 *         evaluationName, evaluationNumber, evaluationDescription, evaluator }
 */
export const updateEvaluation = async (data) => {
  const res = await api.put("/evaluations", data);
  return res.data;
};

/**
 * 評価を削除
 * DELETE /evaluations/{evaluationId}
 */
export const deleteEvaluation = async (evaluationId) => {
  const res = await api.delete(`/evaluations/${evaluationId}`);
  return res.data;
};

// ── アイデア ──

/**
 * 企業のアイデア一覧を取得
 * GET /ideas/{companyId}
 */
export const fetchIdeas = async (companyId) => {
  const res = await api.get(`/ideas/${companyId}`);
  return res.data;
};

/**
 * アイデア詳細を取得
 * GET /ideas/idea/{ideaId}
 */
export const fetchIdea = async (ideaId) => {
  const res = await api.get(`/ideas/idea/${ideaId}`);
  return res.data;
};

/**
 * アイデアを登録
 * POST /ideas
 */
export const createIdea = async (data) => {
  const res = await api.post("/ideas", data);
  return res.data;
};

/**
 * アイデアを更新
 * PUT /ideas
 */
export const updateIdea = async (data) => {
  const res = await api.put("/ideas", data);
  return res.data;
};

/**
 * アイデアを削除
 * DELETE /ideas/{ideaId}
 */
export const deleteIdea = async (ideaId) => {
  const res = await api.delete(`/ideas/${ideaId}`);
  return res.data;
};

// ── 気づき（Awareness）──

/**
 * メンバーの気づき一覧を取得
 * GET /awarenesses/{memberId}
 */
export const fetchAwarenesses = async (memberId) => {
  const res = await api.get(`/awarenesses/${memberId}`);
  return res.data;
};

/**
 * 気づきを登録
 * POST /awarenesses
 */
export const createAwareness = async (data) => {
  const res = await api.post("/awarenesses", data);
  return res.data;
};

/**
 * 気づきを更新
 * PUT /awarenesses
 */
export const updateAwareness = async (data) => {
  const res = await api.put("/awarenesses", data);
  return res.data;
};

/**
 * 気づきを削除
 * DELETE /awarenesses/{awarenessId}
 */
export const deleteAwareness = async (awarenessId) => {
  const res = await api.delete(`/awarenesses/${awarenessId}`);
  return res.data;
};

// ── 市場登録 ──

/**
 * メンバーのワーカーデータを取得（市場登録用）
 * GET /members/workers/{memberId}
 */
export const fetchMemberWithWorkers = async (memberId) => {
  const res = await api.get(`/members/workers/${memberId}`);
  return res.data;
};

/**
 * メンバー実績を市場に登録
 * POST /members/workers（市場サーバー）
 */
export const registerMemberAchievementsToMarket = async (memberData) => {
  const res = await marketApi.post("/members/workers", memberData);
  return res.data;
};

// ── 設計・戦略 参照用API ──

// Business Domain
export const fetchBusinessDomains = async (companyId) => {
  const res = await api.get(`/businessDomains/company/${companyId}`);
  return res.data;
};
export const fetchBusinessDomain = async (id) => {
  const res = await api.get(`/businessDomains/${id}`);
  return res.data;
};

// Strategy Map (設計用: successFactors by businessDomain)
export const fetchSmFinancialGoals = async (businessDomainId) => {
  const res = await api.get(`/financialGoal/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchSmCustomerValues = async (businessDomainId) => {
  const res = await api.get(`/customerValue/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchSmStrategicGoals = async (businessDomainId) => {
  const res = await api.get(`/strategicGoal/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchSmStrategicIssues = async (businessDomainId) => {
  const res = await api.get(`/strategicIssue/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchSmHumanCapitalGoals = async (businessDomainId) => {
  const res = await api.get(`/humanCapitalGoal/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchSmInfoCapitalGoalsApp = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalGoal/app/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchSmInfoCapitalGoalsData = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalGoal/data/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchSmInfoCapitalGoalsIt = async (businessDomainId) => {
  const res = await api.get(`/informationCapitalGoal/it/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchSmOrgCapitalGoals = async (businessDomainId) => {
  const res = await api.get(`/organizationCapitalGoal/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchSuccessFactor = async (id) => {
  const res = await api.get(`/successFactor/${id}`);
  return res.data;
};

// Asset Types (設計)
export const fetchCustomerTypes = async (businessDomainId) => {
  const res = await api.get(`/customerTypes/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchCustomerType = async (id) => {
  const res = await api.get(`/customerTypes/${id}`);
  return res.data;
};
export const fetchProductTypes = async (businessDomainId) => {
  const res = await api.get(`/productTypes/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchProductType = async (id) => {
  const res = await api.get(`/productTypes/${id}`);
  return res.data;
};
export const fetchMemberTypes = async (businessDomainId) => {
  const res = await api.get(`/memberTypes/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchMemberType = async (id) => {
  const res = await api.get(`/memberTypes/memberType/${id}`);
  return res.data;
};
export const fetchPartnerTypes = async (businessDomainId) => {
  const res = await api.get(`/partnerTypes/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchPartnerType = async (id) => {
  const res = await api.get(`/partnerTypes/partnerType/${id}`);
  return res.data;
};
export const fetchDataDomains = async (companyId) => {
  const res = await api.get(`/dataDomains/company/${companyId}`);
  return res.data;
};
export const fetchDataDomain = async (id) => {
  const res = await api.get(`/dataDomains/${id}`);
  return res.data;
};
export const fetchDataTypes = async (businessDomainId) => {
  const res = await api.get(`/dataTypes/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchDataType = async (id) => {
  const res = await api.get(`/dataTypes/${id}`);
  return res.data;
};
export const fetchDataItemsByDataType2 = async (dataTypeId) => {
  const res = await api.get(`/dataItems/dataType/${dataTypeId}`);
  return res.data;
};
export const fetchDataItem = async (id) => {
  const res = await api.get(`/dataItems/${id}`);
  return res.data;
};
export const fetchApplicationTypes = async (businessDomainId) => {
  const res = await api.get(`/applicationTypes/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchApplicationType = async (id) => {
  const res = await api.get(`/applicationTypes/${id}`);
  return res.data;
};
export const fetchItTypes = async (businessDomainId) => {
  const res = await api.get(`/itTypes/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchItType = async (id) => {
  const res = await api.get(`/itTypes/${id}`);
  return res.data;
};
export const fetchFinancialAssetTypes = async (businessDomainId) => {
  const res = await api.get(`/financialAssetTypes/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchFinancialAssetType = async (id) => {
  const res = await api.get(`/financialAssetTypes/${id}`);
  return res.data;
};
export const fetchPlaceTypes = async (businessDomainId) => {
  const res = await api.get(`/placeTypes/businessDomain/${businessDomainId}`);
  return res.data;
};
export const fetchPlaceType = async (id) => {
  const res = await api.get(`/placeTypes/${id}`);
  return res.data;
};
export const fetchJobs = async (businessDomainId) => {
  const res = await api.get(`/jobs/business_domain/${businessDomainId}`);
  return res.data;
};
export const fetchJob = async (id) => {
  const res = await api.get(`/jobs/${id}`);
  return res.data;
};
export const fetchTasks = async (jobId) => {
  const res = await api.get(`/tasks/job/${jobId}`);
  return res.data;
};
export const fetchTask = async (id) => {
  const res = await api.get(`/tasks/${id}`);
  return res.data;
};
export const fetchBusinessProcesses = async (businessDomainId) => {
  const res = await api.get(`/businessProcesses/notValueChain/${businessDomainId}`);
  return res.data;
};
export const fetchBusinessProcess = async (id) => {
  const res = await api.get(`/businessProcesses/${id}`);
  return res.data;
};
export const fetchValueChains = async (businessDomainId) => {
  const res = await api.get(`/businessProcesses/valueChain/${businessDomainId}`);
  return res.data;
};

// Business Unit (戦略)
export const fetchBusinessUnits = async (companyId) => {
  const res = await api.get(`/businessUnits/company/${companyId}`);
  return res.data;
};
export const fetchBusinessUnit = async (id) => {
  const res = await api.get(`/businessUnits/${id}`);
  return res.data;
};

// BSC Goals (戦略)
export const fetchBscFinancialGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/financialGoal/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscCustomerValueGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/customerValue/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscStrategicGoalGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/strategicGoal/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscStrategicIssueGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/strategicIssue/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscHumanCapitalGoalGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/humanCapitalGoal/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscInfoCapitalGoalAppGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/informationCapitalGoalApp/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscInfoCapitalGoalDataGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/informationCapitalGoalData/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscInfoCapitalGoalItGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/informationCapitalGoalIt/goal/${businessUnitId}`);
  return res.data;
};
export const fetchBscOrgCapitalGoalGoals = async (businessUnitId) => {
  const res = await api.get(`/businessUnit/organizationCapitalGoal/goal/${businessUnitId}`);
  return res.data;
};

// Strategy Categories
export const fetchCustomerCategories = async (businessUnitId) => {
  const res = await api.get(`/customerCategories/businessUnit/${businessUnitId}`);
  return res.data;
};
export const fetchCustomerCategory = async (id) => {
  const res = await api.get(`/customerCategories/${id}`);
  return res.data;
};
export const fetchProductCategories = async (businessUnitId) => {
  const res = await api.get(`/productCategories/businessUnit/${businessUnitId}`);
  return res.data;
};
export const fetchProductCategory = async (id) => {
  const res = await api.get(`/productCategories/${id}`);
  return res.data;
};
export const fetchProductFunctions = async (categoryId) => {
  const res = await api.get(`/productFunctions/product_category/${categoryId}`);
  return res.data;
};
export const fetchMemberCategories = async (companyId) => {
  const res = await api.get(`/memberCategories/company/${companyId}`);
  return res.data;
};
export const fetchMemberCategory = async (id) => {
  const res = await api.get(`/memberCategories/${id}`);
  return res.data;
};
export const fetchPartnerCategories = async (companyId) => {
  const res = await api.get(`/partnerCategories/company/${companyId}`);
  return res.data;
};
export const fetchPartnerCategory = async (id) => {
  const res = await api.get(`/partnerCategories/${id}`);
  return res.data;
};
export const fetchDataCategories = async (businessUnitId) => {
  const res = await api.get(`/dataCategories/businessUnit/${businessUnitId}`);
  return res.data;
};
export const fetchDataCategory = async (id) => {
  const res = await api.get(`/dataCategories/${id}`);
  return res.data;
};
export const fetchApplicationCategories = async (companyId) => {
  const res = await api.get(`/applicationCategories/company/${companyId}`);
  return res.data;
};
export const fetchApplicationCategory = async (id) => {
  const res = await api.get(`/applicationCategories/${id}`);
  return res.data;
};
export const fetchApplicationFunctions = async (categoryId) => {
  const res = await api.get(`/applicationFunctions/applicationCategory/${categoryId}`);
  return res.data;
};
export const fetchApplicationFunction = async (id) => {
  const res = await api.get(`/applicationFunctions/${id}`);
  return res.data;
};
export const fetchItCategories = async (companyId) => {
  const res = await api.get(`/itCategories/company/${companyId}`);
  return res.data;
};
export const fetchItCategory = async (id) => {
  const res = await api.get(`/itCategories/${id}`);
  return res.data;
};
export const fetchFinancialAssetCategories = async (companyId) => {
  const res = await api.get(`/financialAssetCategories/company/${companyId}`);
  return res.data;
};
export const fetchFinancialAssetCategory = async (id) => {
  const res = await api.get(`/financialAssetCategories/${id}`);
  return res.data;
};
export const fetchPlaceCategories = async (companyId) => {
  const res = await api.get(`/placeCategories/company/${companyId}`);
  return res.data;
};
export const fetchPlaceCategory = async (id) => {
  const res = await api.get(`/placeCategories/${id}`);
  return res.data;
};
export const fetchDepartmentsByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/departments/business_unit/${businessUnitId}`);
  return res.data;
};
export const fetchDepartment = async (id) => {
  const res = await api.get(`/departments/${id}`);
  return res.data;
};
export const fetchActionsByDepartment = async (departmentId) => {
  const res = await api.get(`/actions/department/${departmentId}`);
  return res.data;
};
export const fetchActionPlansByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/actionPlans/businessUnit/${businessUnitId}`);
  return res.data;
};
export const fetchActionsForActionPlan = async (actionPlanId) => {
  const res = await api.get(`/actions/actionPlan/${actionPlanId}`);
  return res.data;
};
export const fetchProjectsByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/project/business_unit/${businessUnitId}`);
  return res.data;
};
export const fetchProgramsByBusinessUnit = async (businessUnitId) => {
  const res = await api.get(`/program/business_unit/${businessUnitId}`);
  return res.data;
};

// ── ナレッジエージェント ──
export const fetchAgentsByWork = async (workId) => {
  const res = await api.get(`/agents/work/${workId}`);
  return res.data;
};
